/**
 * baseDualRead.js — Phase 6 cutover: single live tenant root (`_bases`).
 */

import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

const MEMBER_PATH_RE = /^_bases\/([^/]+)\/members\/([^/]+)$/;

function cleanKey(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function baseRootDocRef(baseKey) {
  return doc(db, collections.bases, cleanKey(baseKey));
}

/**
 * Extract a tenant id from a collectionGroup('members') hit.
 *
 * @param {import('firebase/firestore').QueryDocumentSnapshot|import('firebase/firestore').DocumentSnapshot} memberDoc
 * @returns {string}
 */
export function resolveBaseKeyFromMemberDoc(memberDoc) {
  const path = String(memberDoc?.ref?.path || '');
  const match = path.match(MEMBER_PATH_RE);
  if (!match) return '';

  const data = memberDoc.data() || {};
  return cleanKey(data.baseKey) || cleanKey(match[1]);
}

/**
 * @param {Array<import('firebase/firestore').QueryDocumentSnapshot>} memberDocs
 * @returns {string[]}
 */
export function uniqueBaseKeysFromMemberDocs(memberDocs) {
  const keys = new Set();
  for (const memberDoc of memberDocs || []) {
    const key = resolveBaseKeyFromMemberDoc(memberDoc);
    if (!key) continue;
    keys.add(key);
  }
  return Array.from(keys);
}

/**
 * @param {string} baseKey
 * @returns {Promise<{ snap: import('firebase/firestore').DocumentSnapshot, source: 'bases' }|null>}
 */
export async function getBaseDocSnap(baseKey) {
  const key = cleanKey(baseKey);
  if (!key) return null;

  const snap = await getDoc(baseRootDocRef(key));
  if (!snap.exists()) return null;
  return { snap, source: 'bases' };
}

/**
 * @param {string} baseKey
 * @param {(snap: import('firebase/firestore').DocumentSnapshot|null, source: 'bases'|null) => void} onNext
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToBaseDoc(baseKey, onNext, onError) {
  const key = cleanKey(baseKey);
  if (!key || typeof onNext !== 'function') {
    return () => {};
  }

  return onSnapshot(
    baseRootDocRef(key),
    (snap) => {
      onNext(snap.exists() ? snap : null, 'bases');
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
    },
  );
}

/**
 * Owner query against the live tenant root.
 *
 * @param {string} ownerKey
 */
export function ownerBaseQueries(ownerKey) {
  const cleaned = cleanKey(ownerKey);
  const liveQuery = query(
    collection(db, collections.bases),
    where('ownerKey', '==', cleaned),
  );
  return {
    basesQuery: liveQuery,
  };
}
